// This is a translated function from the pbsorg codebase called "flatten_video_info" that can be found here:
// https://github.com/pbs-digital/pbsorg/blob/main/core/utils.py#L373 (as of this writing)
// It is used to translate the kind-of-weird data that Content Service has for Video Objects
// into something more usable / approachable for the front-end

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { Parent, ParentSeason, SeasonShow, VideoClass, VideoFranchise, VideoTypeEnum } from '@/lib/types/api/video';
import { ContentServiceVideoObject } from '@/lib/types/api/content-service';
import { FormatOptions, formatVideoMetadata } from '@/lib/helpers/format-video-metadata';
import { find } from '@/lib/helpers/find';
import { getBestVideoImage } from '@/lib/helpers/get-best-video-image';
import { getMetaTitle } from '@/lib/helpers/get-meta-title';
import { getLocalContentStationIds, getLocalContentStations } from './get-local-content-stations';
import { getSocialLinks } from '@/lib/helpers/get-social-links';

type Episode = {
  ordinal: number;
  slug: string;
  title: string;
  season: ParentSeason;
  cid: string;
  resource_type: string;
};

function iso8601ToDatetime(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
* Extract parent entities from the parent object
*/
function extractParentEntities(parent: Parent | undefined) {
  if (!parent) {
    return {
      parentResourceType: undefined,
      season: null,
      show: null,
      episode: null,
      franchise: null
    };
  }

  const parentResourceType = parent.resource_type;

  return {
    parentResourceType,
    season: parentResourceType === "season" ? parent as unknown as ParentSeason : find("season", parent) as ParentSeason | null,
    show: parentResourceType === "show" ? parent as unknown as SeasonShow : find("show", parent) as SeasonShow | null,
    episode: parentResourceType === "episode" ? parent as Episode : find("episode", parent) as Episode | null,
    franchise: parentResourceType === "franchise" ? parent as unknown as VideoFranchise :find("franchise", parent) as VideoFranchise | null
  };
}

/**
* Calculate episode metadata from premiere date and episode info
*/
function calculateEpisodeMeta(
  show: SeasonShow | null,
  episode: Episode | null,
  premiereDate: Date | null
): number | string {
  const formattedPremiereDate = premiereDate?.toLocaleDateString('en-US') || '';

  if (show?.display_episode_number !== false) {
    return typeof episode === "number" ? episode : episode?.ordinal || "";
  }

  return formattedPremiereDate;
}

/**
* Build show data object from parent entities
*/
function buildShowData(
  show: SeasonShow | null,
  season: ParentSeason | null,
  episode: Episode | null,
  premiereDate: Date | null
) {
  if (!show) return undefined;

  const episodeMeta = calculateEpisodeMeta(show, episode, premiereDate);

  return {
    slug: show.slug || "",
    title: show.title || "",
    season: typeof season === "number" ? season : season?.ordinal,
    episode: episodeMeta,
    hashtag: show.hashtag || undefined,
    social_links: show.links && getSocialLinks(show.links) || undefined,
    seasons_count: show.seasons_count || 0,
    // some show objects have a .logos object, some have .images -
    // this papers over this inconsistency
    images: { ...show.logos, ...show.images },
    display_episode_number: show.display_episode_number,
  };
}

/**
* Build franchise data object
*/
function buildFranchiseData(franchise: VideoFranchise | null) {
  if (!franchise || Object.keys(franchise).length === 0) return undefined;

  return {
    title: franchise.title,
    slug: franchise.slug,
    logo: franchise.images?.["franchise-color-logo"] || "",
    logo_cropped: franchise.images?.["color-logo-41"] || "",
    logo_cropped_white: franchise["logo_cropped_white"] || franchise.images?.["white-logo-41"] || "",
    image: franchise.images?.["franchise-poster2x3"] || "",
  };
}

/**
* Build ancestor data (show or franchise) for the video
*/
function buildAncestorData(show: SeasonShow | null, franchise: VideoFranchise | null, videoData: VideoClass) {
  if (show) {
    return {
      ancestor_title: show.title || "",
      ancestor_slug: show.slug || "",
      ancestor_type: "show" as const,
      ancestor_white_logo: videoData.show?.images?.["white-logo-41"] || videoData.show?.images?.["show-white-logo"],
      genre: show.genre,
      ga_events: {
        show_page_tracking_code: show.tracking_ga_page
      }
    };
  }

  if (franchise) {
    return {
      ancestor_title: franchise.title || "",
      ancestor_slug: franchise.slug || "",
      ancestor_type: "franchise" as const,
      ancestor_white_logo: videoData.franchise?.logo_cropped_white,
      genre: undefined,
      ga_events: {
        show_page_tracking_code: franchise.tracking_ga_page
      }
    };
  }

  return {
    ancestor_title: "",
    ancestor_slug: "",
    ancestor_type: undefined,
    ancestor_white_logo: undefined,
    genre: undefined,
    ga_events: undefined
  };
}

/**
* Extract learning media URL from show links
*/
function extractLearningMediaUrl(show: SeasonShow | null): string | undefined {
  return show?.links?.find(link => link.profile === 'learning-media-content')?.value;
}

/**
* Process related promos to add image URLs and video URLs
*/
function processRelatedPromos(relatedPromos: ContentServiceVideoObject['related_promos']): void {
  if (!relatedPromos) return;

  for (const relatedPromo of relatedPromos) {
    if (relatedPromo.item_type === "related_video_asset") {
      if (relatedPromo.images) {
        relatedPromo.image_url = getBestVideoImage(relatedPromo.images);
      }
      if (relatedPromo.slug) {
        relatedPromo.url = `/video/${relatedPromo.slug}`;
      }
    }
  }
}


/**
* Takes a raw Content Service video collection and normalizes it into a usable format,
* matching what was done in the pbsorg django codebase
* @param {ContentServiceVideoObject} videoDataRaw - Raw video data from Content Service
* @param {FormatOptions} [format="short"] - Format option for metadata
* @param {boolean} [includeDuration=true] - Whether to include duration in summary
* @returns {VideoClass} Normalized video data
*/
const normalizeVideoData = (
  videoDataRaw: ContentServiceVideoObject,
  format: FormatOptions = "short",
  includeDuration = true
): VideoClass => {
  /** TypeScript is complaining that we are casting the videoData variable as a VideoClass
  * When it starts off as a raw CS video object, the missing fields are later added
  * in this function, but TS doesn't know that :)
  */
  const videoData = { ...videoDataRaw } as unknown as VideoClass;

  // Because the raw CS object contains "parent", we start with it because of the spread operator
  // but it's confusing later, so we delete it from the videoData object.
  delete videoData.parent;

  // Extract parent entities
  const parent = videoDataRaw.parent;
  const { parentResourceType, season, show, episode, franchise } = extractParentEntities(parent);

  // Handle video type transformation
  if (videoDataRaw.video_type === "full_length") {
    // This type casting is okay because what we're after is transforming
    // the video_type to either "episode" or "special" when it starts out as "full_length" -
    // CS just works this way.
    videoData.video_type = parentResourceType as VideoTypeEnum;
  }
  videoData.parent_type = parentResourceType || "video";

  // Build nested data structures
  const premiereDate = iso8601ToDatetime(videoDataRaw.premiere_date as unknown as string);

  // Build shows and franchise data, which is used to create ancestor data for the object.
  videoData.show = buildShowData(show, season, episode, premiereDate);
  videoData.franchise = buildFranchiseData(franchise);

  // Set ancestor data
  // Why "ancestor"? Because a video can belong to either a show OR a franchise, and
  // we need to be able to display information from either.
  const ancestorData = buildAncestorData(show, franchise, videoData);
  Object.assign(videoData, ancestorData);

  // Set computed fields
  videoData.local_content_stations = show?.audience && getLocalContentStations(show.audience);
  videoData.local_content_station_ids = show?.audience && getLocalContentStationIds(show.audience);
  videoData.summary = formatVideoMetadata(videoData, format, includeDuration);
  videoData.image = getBestVideoImage(videoDataRaw.images) || "";
  videoData.learning_media_url = extractLearningMediaUrl(show);
  videoData.meta_title = getMetaTitle(videoData);

  // Direct assignments from raw data
  videoData.legacy_tp_media_id = videoDataRaw.legacy_tp_media_id!;
  videoData.cid = videoDataRaw.cid;
  videoData.flags = videoDataRaw.flags;
  videoData.links = videoDataRaw.links?.length ? videoDataRaw.links : undefined;

  // Process related promos
  processRelatedPromos(videoDataRaw.related_promos);

  return videoData;
}

export {
  normalizeVideoData,
  extractParentEntities,
  calculateEpisodeMeta,
  buildShowData,
  buildFranchiseData,
  buildAncestorData,
  extractLearningMediaUrl,
  processRelatedPromos
};
